import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { classifyLinkViewError, ShareLinkViewErrorScreen, type ShareLinkViewErrorKind } from '@indipia/shared-features';
import { worklogApi } from '../api/worklogApi';
import { CustomerReportPublicBody } from './CustomerReportSharePage';
import type { CustomerReportPublic } from '../types/worklog.types';

/**
 * 고객 리포트 발송자 미리보기(새 창) — /agents/work-log/customer-reports/preview?historyId={발송 이력 번호}.
 * 공유 이력의 고객 리포트 행 [미리보기]가 연다. 발송 당시 스냅샷을 "보낸 그대로" 보여 주며,
 * 로그인 토큰으로 조회하므로 고객 열람 기록이 남지 않는다(GET /share-history/{id}/preview).
 */
export function CustomerReportSendPreviewPage() {
  const [params] = useSearchParams();
  const raw = params.get('historyId');
  const historyId = raw !== null && /^\d+$/.test(raw) ? Number(raw) : null;

  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [data, setData] = useState<CustomerReportPublic | null>(null);
  const [errorKind, setErrorKind] = useState<ShareLinkViewErrorKind>('TEMPORARY');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (historyId === null) {
      setErrorKind('INVALID');
      setState('error');
      return;
    }
    const controller = new AbortController();
    setState('loading');
    worklogApi
      .getShareHistoryPreview(historyId, controller.signal)
      .then((res) => {
        setData(res.data.data);
        setState('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setErrorKind(classifyLinkViewError(err));
        setState('error');
      });
    return () => controller.abort();
  }, [historyId, reloadKey]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <p className="text-sm text-text-secondary">리포트를 불러오는 중…</p>
      </div>
    );
  }

  if (state === 'error' || !data) {
    return errorKind === 'SESSION' ? (
      <ShareLinkViewErrorScreen
        kind="SESSION"
        title="로그인이 필요합니다"
        description="발송자 본인 계정으로 앱에 로그인한 뒤 다시 시도해 주세요."
      />
    ) : (
      <ShareLinkViewErrorScreen kind={errorKind} onRetry={() => setReloadKey((k) => k + 1)} />
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="border-b border-border bg-surface-raised px-4 py-2 text-center text-body-sm font-bold text-text-secondary">
        미리보기 · 발송 당시 내용 그대로 보여요
      </div>
      <CustomerReportPublicBody data={data} />
    </div>
  );
}
